import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Chip,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ApiService from '../../services/api';

const audienceOptions = [
  { value: 'all_employers', label: 'All Employers' },
  { value: 'all_jobseekers', label: 'All Job Seekers' },
  { value: 'individual', label: 'Individual Emails' },
];

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

const Communication = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [communications, setCommunications] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });

  const [audienceType, setAudienceType] = useState('all_employers');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [individualEmails, setIndividualEmails] = useState([]);
  const [individualInput, setIndividualInput] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const handleEdit = useCallback((row) => {
    setEditingId(row.id);
    setSubject(row.subject);
    setAudienceType(
      row.audience === 'All Employers' ? 'all_employers' :
      row.audience === 'All Job Seekers' ? 'all_jobseekers' : 'individual'
    );
  }, []);

  

  const mapAudience = (a) => {
    switch (a) {
      case 'all_employers': return 'All Employers';
      case 'all_jobseekers': return 'All Job Seekers';
      case 'individual': return 'Individual';
      default: return a;
    }
  };

  const fetchList = useCallback(async (page = 1, limit = 10) => {
    setListLoading(true);
    try {
      const key = String(category || '').toLowerCase();
      const statusMap = { sent: 'sent', draft: 'draft', drafts: 'draft', failed: 'failed', fail: 'failed', auto: 'scheduled', scheduled: 'scheduled' };
      const status = statusMap[key];
      const resp = await ApiService.getCommunications({ page, limit, ...(status ? { status } : {}) });
      const items = resp.communications || resp.items || [];
      const mapped = items.map((c) => ({
        id: c._id || c.id,
        subject: c.subject,
        audience: mapAudience(c.audienceType),
        totalRecipients: c.totalRecipients ?? (Array.isArray(c.individualEmails) ? c.individualEmails.length : c.recipientsCount || 0),
        recipientsCount: c.recipientsCount || 0,
        failedCount: c.failedCount ?? Math.max(((Array.isArray(c.individualEmails) ? c.individualEmails.length : 0) - (c.recipientsCount || 0)), 0),
        sentBy: c.sentBy,
        status: c.status,
        sentAt: c.sentAt,
        bodyHtml: c.bodyHtml,
        bodyText: c.bodyText,
        lastError: c.lastError
      }));
      setCommunications(mapped);
      const p = resp.pagination || { page, limit, total: mapped.length };
      setPagination(p);
    } catch (e) {
      console.error('Failed to fetch communications', e);
    } finally {
      setListLoading(false);
    }
  }, [category]);

  useEffect(() => { fetchList(1, 10); }, [fetchList]);

  const handleResend = useCallback(async (row) => {
    try {
      await ApiService.resendCommunication(row.id);
      await fetchList(pagination.page, pagination.limit);
    } catch (e) {
      console.error('Failed to resend', e);
    }
  }, [fetchList, pagination.page, pagination.limit]);

  const columns = useMemo(() => ([
    { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200 },
    { field: 'audience', headerName: 'Audience', width: 180 },
    { field: 'totalRecipients', headerName: 'Total Recipients', width: 150 },
    { field: 'recipientsCount', headerName: 'Sent', width: 110 },
    { field: 'failedCount', headerName: 'Failed', width: 110 },
    { field: 'sentBy', headerName: 'Sent By', width: 220, valueGetter: (params) => {
      const sb = params.row.sentBy;
      return sb ? `${sb.firstName || ''} ${sb.lastName || ''}`.trim() || sb.email : '—';
    } },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'sentAt', headerName: 'Sent At', width: 200, valueGetter: (params) => {
      return params.row.sentAt ? new Date(params.row.sentAt).toLocaleString() : '—';
    } },
    {
      field: 'actions', headerName: 'Actions', width: 220, renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => handleEdit(params.row)}>Edit</Button>
          <Button size="small" variant="contained" onClick={() => handleResend(params.row)}>Resend</Button>
        </Stack>
      )
    }
  ]), [handleEdit, handleResend]);

  const handleAddEmailChip = (value) => {
    const v = (value || '').trim();
    if (!v) return;
    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(v)) return;
    if (!individualEmails.includes(v)) {
      setIndividualEmails([...individualEmails, v]);
    }
  };

  const handleRemoveEmailChip = (email) => {
    setIndividualEmails(individualEmails.filter((e) => e !== email));
  };

  const resetForm = () => {
    setAudienceType('all_employers');
    setSubject('');
    setBodyHtml('');
    setIndividualEmails([]);
    setEditingId(null);
  };

  

  const handleSubmit = async (sendNow) => {
    setLoading(true);
    try {
      // Ensure any typed-but-not-chipped email is included
      let finalIndividualEmails = individualEmails;
      const pending = (individualInput || '').trim();
      if (audienceType === 'individual' && pending) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(pending) && !finalIndividualEmails.includes(pending)) {
          finalIndividualEmails = [...finalIndividualEmails, pending];
        }
      }
      // Require at least one recipient for individual audience
      if (audienceType === 'individual' && finalIndividualEmails.length === 0) {
        throw new Error('Please add at least one recipient email and press Enter.');
      }
      const payload = {
        subject,
        bodyHtml,
        bodyText: stripHtml(bodyHtml),
        audienceType,
        individualEmails: finalIndividualEmails,
        sendNow,
        ...(sendNow ? {} : (scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}))
      };
      if (editingId) {
        await ApiService.updateCommunication(editingId, payload);
      } else {
        await ApiService.createCommunication(payload);
      }
      resetForm();
      await fetchList(pagination.page, pagination.limit);
    } catch (e) {
      console.error('Failed to submit communication', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!category) return communications;
    const key = String(category).toLowerCase();
    if (key === 'sent') return communications.filter(c => c.status === 'sent');
    if (key === 'draft' || key === 'drafts') return communications.filter(c => c.status === 'draft');
    if (key === 'fail' || key === 'failed') return communications.filter(c => c.status === 'failed');
    if (key === 'scheduled' || key === 'auto' || key === 'auto-send' || key === 'autosend') return communications.filter(c => c.status === 'scheduled');
    return communications;
  }, [communications, category]);

  const listTitle = useMemo(() => {
    const key = String(category || '').toLowerCase();
    if (key === 'sent') return 'Sent Communications';
    if (key === 'draft' || key === 'drafts') return 'Draft Communications';
    if (key === 'fail' || key === 'failed') return 'Failed Communications';
    if (key === 'scheduled' || key === 'auto' || key === 'auto-send' || key === 'autosend') return 'Scheduled (Auto Send) Communications';
    return 'Sent Communications';
  }, [category]);

  const isListOnly = useMemo(() => {
    const key = String(category || '').toLowerCase();
    return key === 'sent' || key === 'failed' || key === 'fail' || key === 'draft' || key === 'drafts';
  }, [category]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label="Sent" color={String(category||'').toLowerCase()==='sent' ? 'primary' : 'default'} onClick={() => navigate('/admin/communications/sent')} />
        <Chip label="Drafts" color={['draft','drafts'].includes(String(category||'').toLowerCase()) ? 'primary' : 'default'} onClick={() => navigate('/admin/communications/drafts')} />
        <Chip label="Failed" color={['fail','failed'].includes(String(category||'').toLowerCase()) ? 'primary' : 'default'} onClick={() => navigate('/admin/communications/failed')} />
        <Chip label="Auto Send" color={['auto','scheduled','auto-send','autosend'].includes(String(category||'').toLowerCase()) ? 'primary' : 'default'} onClick={() => navigate('/admin/communications/scheduled')} />
      </Stack>
      <Grid container spacing={2}>
        {!isListOnly && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title={editingId ? 'Edit Communication' : 'New Communication'} subheader="Send emails to user groups or individuals" />
            <CardContent>
              <TextField
                select
                label="Audience"
                value={audienceType}
                onChange={(e) => setAudienceType(e.target.value)}
                fullWidth
                margin="normal"
              >
                {audienceOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fullWidth
                margin="normal"
              />

              {audienceType === 'individual' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Recipient Emails</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {individualEmails.map((email) => (
                      <Chip key={email} label={email} onDelete={() => handleRemoveEmailChip(email)} />
                    ))}
                  </Stack>
                  <TextField
                    placeholder="Type an email and press Enter"
                    fullWidth
                    margin="normal"
                    value={individualInput}
                    onChange={(e) => setIndividualInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmailChip(individualInput);
                        setIndividualInput('');
                      }
                    }}
                  />
                </Box>
              )}

              <TextField
                label="Email Body (HTML)"
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                fullWidth
                margin="normal"
                multiline
                minRows={6}
                placeholder="Use basic HTML for a professional format"
              />

              {(['auto','scheduled','auto-send','autosend'].includes(String(category||'').toLowerCase())) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Schedule send</Typography>
                  <TextField
                    label="Scheduled At"
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              )}


              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => handleSubmit(true)} disabled={loading}>
                  {editingId ? 'Update & Send' : 'Send Now'}
                </Button>
                <Button variant="outlined" onClick={() => handleSubmit(false)} disabled={loading}>
                  {(['auto','scheduled','auto-send','autosend'].includes(String(category||'').toLowerCase())) ? (editingId ? 'Update Schedule' : 'Schedule Send') : (editingId ? 'Update Draft' : 'Save Draft')}
                </Button>
                <Button variant="text" onClick={resetForm} disabled={loading}>Reset</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        )}

        <Grid item xs={12} md={isListOnly ? 12 : 6}>
          <Card>
            <CardHeader title={listTitle} subheader={category === 'scheduled' ? 'Scheduled sending view (auto send)' : 'Edit or resend previous emails'} />
            <CardContent>
              <div style={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  loading={listLoading}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                  onRowClick={(params) => { setDetailRow(params.row); setDetailOpen(true); }}
                />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Communication Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box>
              <Typography variant="subtitle1"><strong>Subject:</strong> {detailRow.subject}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Audience:</strong> {detailRow.audience}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Status:</strong> {detailRow.status}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Sent By:</strong> {detailRow.sentBy ? `${detailRow.sentBy.firstName || ''} ${detailRow.sentBy.lastName || ''}`.trim() || detailRow.sentBy.email : '—'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Sent At:</strong> {detailRow.sentAt ? new Date(detailRow.sentAt).toLocaleString() : '—'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Total:</strong> {detailRow.totalRecipients ?? '—'}
                {'  '}<strong>Sent:</strong> {detailRow.recipientsCount ?? 0}
                {'  '}<strong>Failed:</strong> {detailRow.failedCount ?? 0}
              </Typography>
              {detailRow.lastError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  <strong>Failures:</strong> {detailRow.lastError}
                </Typography>
              )}
              {detailRow.bodyHtml && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Email Body</Typography>
                  <div dangerouslySetInnerHTML={{ __html: detailRow.bodyHtml }} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detailRow && (
            <Button variant="contained" onClick={() => { handleResend(detailRow); }}>Resend</Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Communication;